<script setup lang="ts">
  import { SolDivider, useToast } from '@solfacil/girassol';

  import type { OrderDetailsDelivery } from '~/types/ecommerce/generated.codegen';

  const props = defineProps<{
    delivery: OrderDetailsDelivery;
  }>();

  const { createSuccessToast } = useToast();
  const clipboard = useClipboard();

  const address = computed(() => {
    return props.delivery?.address;
  });

  const completeAddress = computed(() => {
    const {
      street,
      number,
      neighborhood,
      complement,
      zip_code: zipCode,
      city,
      region,
    } = address.value;
    return `${street} ${number ? `, ${number}` : ''} - ${neighborhood} ${complement ? `- ${complement}` : ''} - CEP ${zipCode} - ${city} - ${region}`;
  });

  const cdLocation = computed(() => {
    return `Retirada no CD de ${address.value.city} - ${address.value.region}`;
  });

  const copyAddress = () => {
    clipboard.copy(completeAddress.value);
    createSuccessToast({
      title: 'Copiado',
      description: 'Endereço copiado para a área de transferência',
    });
  };
</script>

<template>
  <div class="delivery-details-order-fob">
    <div class="title-address-order">Endereço de retirada</div>

    <div class="infos-address">
      <div class="address-content">
        <div class="address-column">
          <span class="cd-info">
            {{ cdLocation }}
          </span>

          <div class="cd-address">
            <div class="title-address">Endereço</div>
            <div class="body-address">
              {{ completeAddress }}
            </div>
          </div>
        </div>
        <div class="address-action">
          <icon-girassol:content-copy
            class="icon-size-medium cursor-pointer"
            @click="copyAddress" />
        </div>
      </div>

      <SolDivider thickness="x-small" />

      <div class="retrieval-info">
        <h4 class="retrieval-info__title">
          Informações sobre a retirada do pedido
        </h4>
        <p class="retrieval-info__text">
          Quem for retirar a mercadoria deve usar sapatos fechados, calça e
          camisa. Não é permitido usar bermuda ou camiseta regata. Se o veículo
          não for um caminhão, não nos responsabilizamos por carregar a
          mercadoria; nós a disponibilizamos, e quem retira deve carregá-la no
          veículo.
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .delivery-details-order-fob {
    @apply flex flex-col gap-4xs md:site:gap-2xs;
    @apply border-a-xs rounded-lg border-neutral-high-medium;
    @apply px-4xs py-2xs md:site:px-2xs;

    > .title-address-order {
      @apply fonts-heading-h3 text-brand-primary-dark md:site:text-xs;
    }

    > .infos-address {
      @apply flex flex-col gap-4xs;

      .address-column {
        @apply flex flex-1 flex-col gap-4xs;

        .cd-info {
          @apply fonts-body-large-bold text-neutral-low-dark;
        }

        .cd-address {
          @apply flex flex-col gap-quark;

          .title-address {
            @apply fonts-body-large-bold text-neutral-low-light;
          }

          .body-address {
            @apply fonts-body-large-regular text-neutral-low-light;
          }
        }
      }

      .address-content {
        @apply flex flex-row items-center gap-2xs;
        @apply rounded-md bg-neutral-high-light bg-opacity-intense;
        @apply px-4xs py-4xs;
      }

      .retrieval-info {
        @apply flex flex-col gap-quark;

        &__title {
          @apply fonts-body-large-bold text-brand-primary-pure;
        }

        &__text {
          @apply fonts-body-medium-regular text-brand-primary-pure;
        }
      }
    }
  }
</style>
